import { type ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { mapFilterOption, type FiltersMap, type GenericFiltersMap } from "utils/filterUtils";
import objUtils from "utils/objectUtils";
import {
  setError as setOverviewError,
  setFilterOptions as setOverviewFilterOptions,
  setIsLoading as setOverviewIsLoading,
} from "../slices/campaignFiltersSlice";
import { CampaignFiltersEnum } from "../../types/models";
import { TagsEnumCamelPlural } from "interfaces/TagsEnum";
import { type AccountBase, type PackName } from "interfaces";
import DataService from "features/Application/services/dataServices/typedDataService";
import { pick } from "lodash";
import PromiseStatuses from "enums/promiseStatuses";

export interface GetFilterOptionsProps {
  showPurchased?: boolean;
  filters?: CampaignFiltersEnum[];
}

interface BaseFilterOptionsProps {
  begin: () => void;
  success: (filterOptions: FiltersMap) => void;
  failure: (error: Error) => void;
}

const defaultFiltersOptions = [CampaignFiltersEnum.Publishers, CampaignFiltersEnum.Tags, CampaignFiltersEnum.Packs];

export const getFilterOptions = async (
  props: BaseFilterOptionsProps & GetFilterOptionsProps,
  includePublishers: boolean = true,
) => {
  const { showPurchased, begin, success, failure } = props;
  const selectedFilters = !props.filters?.length ? defaultFiltersOptions : props.filters;

  let filterOptions: FiltersMap = {};

  const tagToFilterMap: { [key in TagsEnumCamelPlural]?: CampaignFiltersEnum } = {
    [TagsEnumCamelPlural.Label]: CampaignFiltersEnum.Labels,
  };

  const readPublishersData = async () => {
    const publishers = await DataService.threatDefence.campaigns.getPublishersFilterOptionsAsync();
    filterOptions = mapFilterOption(
      filterOptions,
      publishers.data,
      CampaignFiltersEnum.Publishers,
      (item: PackName) => ({
        text: item.name,
        value: item.id,
      }),
    );
  };

  const readPacksData = async () => {
    const packs = await DataService.packs.getPacksFilterOptions("campaign", !!showPurchased);
    filterOptions = mapFilterOption(filterOptions, packs.data, CampaignFiltersEnum.Packs, (item: AccountBase) => ({
      text: item.name,
      value: item.id,
    }));
  };

  const readTagsData = async () => {
    const tags = await DataService.threatDefence.campaigns.getTagsFilterOptionsAsync(!!showPurchased);
    for (const [key, value] of objUtils.typedEntries<TagsEnumCamelPlural, string[]>(tags.data)) {
      if (tagToFilterMap[key]) {
        filterOptions = mapFilterOption(filterOptions, value, tagToFilterMap[key], (item: string) => ({
          text: item,
          value: item,
        }));
      }
    }
  };

  const filterHandlers: { [key in CampaignFiltersEnum]?: () => Promise<void> } = {
    [CampaignFiltersEnum.Tags]: readTagsData,
    [CampaignFiltersEnum.Packs]: readPacksData,
    ...(includePublishers && { [CampaignFiltersEnum.Publishers]: readPublishersData }),
  };

  begin();

  const filtersHandlersPromises = Object.values(pick(filterHandlers, selectedFilters)).map(
    (handler: () => Promise<void>) => handler(),
  );

  const result = await Promise.allSettled(filtersHandlersPromises);

  let someFulfilled = false;
  result.forEach((item) => {
    ({
      [PromiseStatuses.rejected]: () => failure((item as PromiseRejectedResult).reason),
      [PromiseStatuses.fulfilled]: () => (someFulfilled = true),
    })[item.status]();
  });

  if (someFulfilled) {
    success(filterOptions);
  }
};

const getFilterOptionsWrapper = (
  setIsLoading: ActionCreatorWithPayload<boolean>,
  setFilterOptions: ActionCreatorWithPayload<GenericFiltersMap<string>>,
  setError: ActionCreatorWithPayload<Error>,
) => {
  return (dispatch: AppDispatch) =>
    getFilterOptions({
      showPurchased: true,
      filters: [CampaignFiltersEnum.Publishers, CampaignFiltersEnum.Tags, CampaignFiltersEnum.Packs],
      begin: () => dispatch(setIsLoading(true)),
      success: (filterOptions: FiltersMap) => {
        dispatch(setFilterOptions(filterOptions));
        dispatch(setIsLoading(false));
      },
      failure: (error: Error) => {
        dispatch(setError(error));
        dispatch(setIsLoading(false));
      },
    });
};

export const getOverviewFilterOptions = () =>
  getFilterOptionsWrapper(setOverviewIsLoading, setOverviewFilterOptions, setOverviewError);
